'use client'

import { toLoginPage } from '@/feature/account/services/account.routing'
import { addToCartDrawerDataAtom } from '@/feature/cart/services/cart.store'
import { useAddToCartTracking } from '@/feature/cart/services/cart.tracking'
import { trackAddToCartInLocalStorage } from '@/feature/cart/services/cart.tracking.addToCart'
import { useAddProductToCart } from '@/feature/product/hooks/useAddProductToCart'
import { useIsActiveHideVatPrice } from '@/feature/product/hooks/useIsActiveHideVatPrice'
import { getProductDisplayPrice } from '@/feature/product/utils/productDisplayPrice'
import { formatPrice } from '@/lib/formatters/currency.formatter'
import { useTranslation } from '@/lib/i18n/useTranslation.client'
import { useIsReactNativeWebView } from '@/lib/react-native/contexts/react-native/ReactNativeProvider.client'
import handleIntent from '@/lib/react-native/services/react-native.intent.client'
import { INTENT_SAME_AS_WEB } from '@/lib/react-native/services/react-native.intent'
import { Button } from '@/ui/buttons/button/Button.client'
import { useSelectable } from '@/ui/helpers/selectable/SelectableContext.client'
import { Icon } from '@/ui/icons/icon/Icon'
import { Link } from '@/ui/links/link/Link.client'
import { createNativeToastIntent } from '@/ui/toast/toast.intents'
import {
  ClickAndCollectStatus,
  type ProductRowReadOnly_CartItemFragment,
} from '@gql-generated/graphql'
import { useSetAtom } from 'jotai'
import { useMemo } from 'react'

const DEFAULT_BUNDLE_QUANTITY = 1

type AddToBasketBundleCardProps = {
  /** The cart id to add the product to. */
  cartId: string | undefined
}

export function AddToBasketBundleCard({
  cartId = '',
}: AddToBasketBundleCardProps): React.ReactNode {
  const { t } = useTranslation(['addToBasketBundleCard', 'generic', 'productPriceLoginLink'])
  const isInWebView = useIsReactNativeWebView()

  const isActiveHideVatPrice = useIsActiveHideVatPrice()
  const { selectedIds, items, isDisabled, setIsDisabled } = useSelectable()!
  const setAddToCartDrawerData = useSetAtom(addToCartDrawerDataAtom)
  const pushAddToCartEvent = useAddToCartTracking()
  const [addProductToCart] = useAddProductToCart()

  /**
   * Maps bundle's selected products ids to an array of cart items
   * ready to feed to add-to-cart drawer
   */
  const addedProducts = useMemo<ProductRowReadOnly_CartItemFragment[]>(() => {
    const itemsByRefMap = new Map(items.map((item) => [item.reference, item]))

    return selectedIds
      .map((selectedProductId) => itemsByRefMap.get(selectedProductId))
      .map((selectedProduct): ProductRowReadOnly_CartItemFragment | undefined => {
        if (!selectedProduct) {
          console.warn('Bundle product not found', selectedProduct)

          return undefined
        }

        if (!selectedProduct.stock?.packaging) {
          console.warn('Bundle product has no packaging', selectedProduct)

          return undefined
        }

        return {
          id: selectedProduct.reference,
          analyticsPromotionStatus: null,
          product: selectedProduct,
          unitPrice: {
            exclTax: selectedProduct.price?.exclTax ?? 0,
          },
          quantity: DEFAULT_BUNDLE_QUANTITY,
          totalExclTax: selectedProduct.price?.exclTax ?? 0,
          discount: {
            catalogDiscountAmount: selectedProduct.price?.discountExclTax || 0,
            percentage: selectedProduct.price?.discountPercentage || 0,
          },
        } satisfies ProductRowReadOnly_CartItemFragment
      })
      .filter((o) => !!o)
  }, [selectedIds, items])

  const handleAddToCartSubmit = async () => {
    setIsDisabled(true)
    let hasClickAndCollectOnlyItems = false

    // TODO: Use the action to add multiple products to the cart when the API provides it.
    for (const selectedId of selectedIds) {
      try {
        await addProductToCart({
          variables: {
            reference: selectedId,
            quantity: DEFAULT_BUNDLE_QUANTITY,
            cartId,
          },
          onCompleted: (outcome) => {
            hasClickAndCollectOnlyItems = outcome.hasClickAndCollectOnlyItems
            const source = items.length === 2 ? 'bundle - 2 produits' : 'bundle - 3 produits'
            trackAddToCartInLocalStorage(selectedIds, source)
            pushAddToCartEvent(
              selectedIds.map((id) => ({
                reference: id,
                quantity: DEFAULT_BUNDLE_QUANTITY,
                source,
                price: 0,
              })),
            )
            setAddToCartDrawerData({
              isOpen: !isInWebView,
              cartItems: addedProducts,
              quantity: DEFAULT_BUNDLE_QUANTITY,
              cartId,
              clickAndCollectStatus: hasClickAndCollectOnlyItems
                ? ClickAndCollectStatus.ClickAndCollectOnly
                : ClickAndCollectStatus.Available,
            })
          },
        })
      } catch (err) {
        console.error('addProductToCartResponse error : ', selectedId, err)
        break
      }
    }

    handleIntent(
      createNativeToastIntent('success', `add-to-cart-bundle-${selectedIds.join('-')}`, {
        title: t('addToBasketBundleCard:addToCart.success.title'),
        message:
          selectedIds.length === 1
            ? t('addToBasketBundleCard:addToCart.success.message.one', {
                reference: selectedIds[0],
              })
            : t('addToBasketBundleCard:addToCart.success.message.other', {
                reference: selectedIds.length,
              }),
      }),
    )

    setIsDisabled(false)
  }

  const bundlePrice = selectedIds.reduce((acc, id) => {
    const item = items.find((item) => item.reference === id)

    return acc + (item ? getProductDisplayPrice(item).price : 0)
  }, 0)

  return (
    <>
      {bundlePrice > 0 && (
        <p className="text-4xl font-bold">
          {formatPrice(bundlePrice)} {!isActiveHideVatPrice && t('generic:excludeTax')}
        </p>
      )}
      {cartId ? (
        <Button
          size="md"
          iconLeft={<Icon.ShoppingBasket />}
          isLoading={isDisabled}
          isDisabled={selectedIds.length === 0}
          onPress={handleAddToCartSubmit}
          className="grow"
          variant="primary"
          intent={INTENT_SAME_AS_WEB}
        >
          {t('addToBasketBundleCard:addToCart', {
            count: selectedIds.length,
          })}
        </Button>
      ) : (
        <Link
          variant="button-primary"
          href={toLoginPage()}
          iconLeft={<Icon.User />}
          className="grow"
        >
          {t('productPriceLoginLink:ctaLogin.short')}
        </Link>
      )}
    </>
  )
}
